import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { getFlag } from '@mahawi/eshop-common/src/get-flag';
import { getHumanReadableTaxValue } from '@mahawi/eshop-common/src/get-human-readable-tax-value';
import { round } from '@mahawi/eshop-common/src/round';
import {
  getLowerCaseLanguageCode,
  getTranslation,
} from '@mahawi/eshop-common/src/translation';
import {
  EWeightUnit,
  type IBasketProduct,
  type ICountry,
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ICurrencyType,
  type IOrderProduct,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Skeleton, Space, Table, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';
import ExternalLink from '../external-link';
import Link from '../link';

interface IDimensions {
  weight: number;
  totalWeight: number;
  weightUnit: EWeightUnit;
}

interface DataType {
  key: string;
  count: number;
  name: string;
  uuid: string;
  price: number;
  priceWithoutTax: number;
  dimensions: IDimensions;
  warehouseUrl: string | undefined;
}

const { Text } = Typography;

function ProductTable({
  products,
  currencyType,
  taxCountryTypeIsoCode,
  withCopyLinks = false,
  productLinkParams = {},
}: {
  products: IOrderProduct[] | IBasketProduct[] | undefined;
  currencyType: ICurrencyType | undefined;
  taxCountryTypeIsoCode: string;
  withCopyLinks?: boolean;
  productLinkParams?: Record<string, string>;
}): React.ReactElement {
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const configUrl = useSelector((s: RootState) => s.Config.url);
  const countries = useSelector((s: RootState) => s.Countries.countries);
  const countriesUpdatedAt = useSelector(
    (s: RootState) => s.Countries.updatedAt,
  );

  const country: ICountry | undefined = useMemo<ICountry | undefined>(():
    ICountry | undefined => {
    if (!countries || !countries.length) return undefined;

    return countries.find(
      (c: ICountry): boolean => c.isoCode === taxCountryTypeIsoCode,
    );
  }, [countries, taxCountryTypeIsoCode, countriesUpdatedAt]);

  const dataSource: DataType[] | undefined = useMemo<
    DataType[] | undefined
  >(() => {
    if (!products) {
      return undefined;
    }

    return products.map((product: IOrderProduct | IBasketProduct) => {
      const name: string | null = getTranslation(product.names, languageType);

      let price: number = 0;
      let priceWithoutTax: number = 0;

      if ('currencyPrices' in product) {
        price =
          product.currencyPrices
            .find(
              ({ isoCode }: ICurrencyPrice): boolean =>
                isoCode === currencyType?.isoCode,
            )
            ?.pricesWithTax.find(
              ({ isoCode }: ICountryCurrencyPrice): boolean =>
                isoCode === taxCountryTypeIsoCode,
            )?.price || 0;
        priceWithoutTax =
          product.currencyPrices.find(
            ({ isoCode }: ICurrencyPrice): boolean =>
              isoCode === currencyType?.isoCode,
          )?.priceWithoutTax || 0;
      } else if ('currencyPrice' in product) {
        price =
          product.currencyPrice.pricesWithTax.find(
            ({ isoCode }: ICountryCurrencyPrice): boolean =>
              isoCode === taxCountryTypeIsoCode,
          )?.price || 0;
        priceWithoutTax = product.currencyPrice.priceWithoutTax || 0;
      }

      return {
        key: product.uuid,
        count: product.count,
        name: name || EMPTY_VALUE,
        uuid: product.uuid,
        price,
        priceWithoutTax,
        dimensions: {
          weight: product.dimensions.weight,
          totalWeight: round(product.dimensions.weight * product.count, 4),
          weightUnit: product.dimensions.weightUnit,
        },
        warehouseUrl:
          'warehouseUrl' in product
            ? (product as IOrderProduct).warehouseUrl
            : undefined,
      };
    });
  }, [products, languageType, currencyType?.isoCode, taxCountryTypeIsoCode]);

  const columns: ColumnsType<DataType> = useMemo<ColumnsType<DataType>>(
    () => [
      {
        title: 'Count',
        dataIndex: 'count',
        key: 'count',
        render: (count: number): React.ReactElement => (
          <Space size={SPACING.sm} align="baseline">
            <Text strong>{count}</Text>
            {withCopyLinks && (
              <CopyToClipboard text={String(count)} size="small" type="link" />
            )}
          </Space>
        ),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (value: string, record: DataType): React.ReactElement => (
          <Space size={SPACING.sm} align="baseline">
            <Link
              to={`/product/${record.uuid}`}
              label={value}
              openInNewTab
              additionalSearchParams={productLinkParams}
            />

            {withCopyLinks && (
              <CopyToClipboard text={value} size="small" type="link" />
            )}

            <ExternalLink
              to={`${configUrl}/${getLowerCaseLanguageCode(languageType)}/product/${record.uuid}`}
              ariaLabel="Open product in new tab"
            />

            {record.warehouseUrl && (
              <ExternalLink
                to={record.warehouseUrl}
                ariaLabel="Open warehouse URL in new tab"
              />
            )}
          </Space>
        ),
      },
      {
        title:
          `Prices with VAT ${getFlag({ countryTypeIsoCode: taxCountryTypeIsoCode })} ${taxCountryTypeIsoCode}`.trim(),
        dataIndex: 'price',
        key: 'price',
        render: (price: number, record: DataType): React.ReactElement => (
          <Space size={SPACING.sm} align="baseline">
            <Space size={SPACING.none} orientation="vertical">
              <Text strong>{formatCurrency(price, currencyType)}</Text>
              <Text>{formatCurrency(price * record.count, currencyType)}</Text>
            </Space>
            {withCopyLinks && (
              <CopyToClipboard text={String(price)} size="small" type="link" />
            )}
          </Space>
        ),
      },
      {
        title: 'Prices without tax',
        dataIndex: 'priceWithoutTax',
        key: 'priceWithoutTax',
        render: (
          priceWithoutTax: number,
          record: DataType,
        ): React.ReactElement => (
          <Space size={SPACING.sm} align="baseline">
            <Space size={SPACING.none} orientation="vertical">
              <Text strong>
                {formatCurrency(priceWithoutTax, currencyType)}
              </Text>
              <Text>
                {formatCurrency(priceWithoutTax * record.count, currencyType)}
              </Text>
            </Space>
            {withCopyLinks && (
              <CopyToClipboard
                text={String(priceWithoutTax)}
                size="small"
                type="link"
              />
            )}
          </Space>
        ),
      },
      {
        title: 'Weight',
        dataIndex: 'dimensions',
        key: 'dimensions.weight',
        render: (dimensions: IDimensions): React.ReactElement => {
          const { weight } = dimensions;

          return (
            <Space size={SPACING.sm} align="baseline">
              <Text strong>
                {weight} {dimensions.weightUnit}
              </Text>
              {withCopyLinks && (
                <CopyToClipboard
                  text={String(weight)}
                  size="small"
                  type="link"
                />
              )}
            </Space>
          );
        },
      },
      {
        title: 'Total weight',
        dataIndex: 'dimensions',
        key: 'dimensions.totalWeight',
        render: (dimensions: IDimensions): React.ReactElement => {
          const { totalWeight } = dimensions;

          return (
            <Space size={SPACING.sm} align="baseline">
              <Text strong>
                {totalWeight} {dimensions.weightUnit}
              </Text>
              {withCopyLinks && (
                <CopyToClipboard
                  text={String(totalWeight)}
                  size="small"
                  type="link"
                />
              )}
            </Space>
          );
        },
      },
    ],
    [
      withCopyLinks,
      currencyType,
      configUrl,
      languageType,
      taxCountryTypeIsoCode,
      productLinkParams,
    ],
  );

  const totalGoodsWeight: number = useMemo<number>((): number => {
    if (!dataSource) return 0;

    return round(
      dataSource.reduce(
        (acc: number, { dimensions: { totalWeight } }: DataType): number =>
          acc + totalWeight,
        0,
      ),
      4,
    );
  }, [dataSource]);

  const totalItemsCount: number = useMemo<number>((): number => {
    if (!dataSource) return 0;

    return dataSource.reduce(
      (acc: number, { count }: DataType): number => acc + count,
      0,
    );
  }, [dataSource]);

  const vatRate: string | null = useMemo<string | null>((): string | null => {
    if (!country || !country.vatRate) {
      return null;
    }

    return getHumanReadableTaxValue(country.vatRate);
  }, [country, taxCountryTypeIsoCode]);

  if (!dataSource) {
    return <Skeleton active />;
  }

  if (dataSource.length === 0) {
    return <Typography.Paragraph>Empty</Typography.Paragraph>;
  }

  return (
    <Space size={SPACING.sm} orientation="vertical" style={{ width: '100%' }}>
      <Table dataSource={dataSource} columns={columns} />

      <Space size={SPACING.sm} align="start">
        <Typography.Text>
          {`VAT rate in ${getFlag({ countryTypeIsoCode: taxCountryTypeIsoCode })} ${taxCountryTypeIsoCode}: ${vatRate ? vatRate : EMPTY_VALUE}`}
        </Typography.Text>
        <CopyToClipboard text={vatRate || ''} size="small" type="link" />
      </Space>

      <Space size={SPACING.sm} align="start">
        <Typography.Text>
          Total goods weight: {totalGoodsWeight} kg
        </Typography.Text>
        <CopyToClipboard
          text={String(totalGoodsWeight)}
          size="small"
          type="link"
        />
      </Space>
      <Space size={SPACING.sm} align="start">
        <Typography.Text>Total items count: {totalItemsCount}</Typography.Text>
        <CopyToClipboard
          text={String(totalItemsCount)}
          size="small"
          type="link"
        />
      </Space>
    </Space>
  );
}

export default ProductTable;
